import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  fetchCorporateTpaList,
  editCorporateTpaList,
  updateCorporateTpaList,
  fetchInsuranceTpaList,
  editInsuranceTpaList,
  updateInsuranceTPAList
} from "../../Adapters/ApiAdapter";
import { toast, ToastContainer } from "react-toastify";
import dayjs from "dayjs";
import "react-toastify/dist/ReactToastify.css";
import CKEditorField from "../../components/CKEditorField";
import { baseUrl } from "../../variables/Variables";

const CorporateTpaList = () => {
  const [corporateList, setCorporateList] = useState([]);
  const [tpaList, setTpaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null); // { type: 'corporate' | 'tpa', data: {} }
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const corp = await fetchCorporateTpaList();
        const tpa = await fetchInsuranceTpaList();
        setCorporateList(corp.data || []);
        setTpaList(tpa.data || []);
      } catch (err) {
        toast.error("Failed to fetch Corporate or TPA data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = async (type, id) => {
    try {
      const response =
        type === "corporate"
          ? await editCorporateTpaList(id)
          : await editInsuranceTpaList(id);

      setEditDialog({ type, data: response.data });
    } catch (err) {
      toast.error("Failed to load data for editing.");
    }
  };

  const handleSave = async () => {
    setLoadingAction(true);
    const formData = new FormData();
    const { type, data } = editDialog;

    formData.append(type === "corporate" ? "corporate_name" : "name", data[type === "corporate" ? "corporate_name" : "name"]);

    try {
      const response =
        type === "corporate"
          ? await updateCorporateTpaList(data.id, formData)
          : await updateInsuranceTPAList(data.id, formData);

      toast.success(`${type === "corporate" ? "Corporate" : "TPA"} updated successfully!`);

      if (type === "corporate") {
        const updatedList = await fetchCorporateTpaList();
        setCorporateList(updatedList.data || []);
      } else {
        const updatedList = await fetchInsuranceTpaList();
        setTpaList(updatedList.data || []);
      }

      setEditDialog(null);
    } catch (err) {
      toast.error("Failed to save changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columnsCorporate = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "corporate_name",
      headerName: "Corporate Name",
      flex: 1,
      renderCell: (params) => (
        <div
          dangerouslySetInnerHTML={{ __html: params.row.corporate_name }}
        />
      ),
    },
    // {
    //   field: "created_at",
    //   headerName: "Created At",
    //   width: 180,
    //   renderCell: (params) =>
    //     dayjs(params.row.created_at).format("YYYY-MM-DD HH:mm:ss"),
    // },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleEdit("corporate", params.row.id)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const columnsTpa = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "name",
      headerName: "TPA Insurance Name",
      flex: 1,
      renderCell: (params) => (
        <div dangerouslySetInnerHTML={{ __html: params.row.name }} />
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 180,
      renderCell: (params) =>
        dayjs(params.row.created_at).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleEdit("tpa", params.row.id)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Corporate List
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={corporateList}
          columns={columnsCorporate}
          autoHeight
          getRowId={(row) => row.id}
        />
      )}

      <Box mt={4}>
        <Typography variant="h4" gutterBottom>
          TPA Insurance List
        </Typography>
        <DataGrid
          rows={tpaList}
          columns={columnsTpa}
          autoHeight
          getRowId={(row) => row.id}
        />
      </Box>

      {editDialog && (
        <Dialog open onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit {editDialog.type === "corporate" ? "Corporate" : "TPA Insurance"}
          </DialogTitle>
          <DialogContent>
            {/* <ReactQuill
              theme="snow"
              value={
                editDialog.data[
                  editDialog.type === "corporate" ? "corporate_name" : "name"
                ]
              }
              onChange={(val) =>
                setEditDialog({
                  ...editDialog,
                  data: {
                    ...editDialog.data,
                    [editDialog.type === "corporate" ? "corporate_name" : "name"]: val,
                  },
                })
              }
              style={{ height: "200px", marginBottom: "30px" }}
            /> */}
            <CKEditorField
            value={editDialog.data[editDialog.type === "corporate" ? "corporate_name" : "name"]}
            onChange={(data) => setEditDialog((prev) => ({ ...prev, data: { ...prev.data, [editDialog.type === "corporate" ? "corporate_name" : "name"]: data } }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=corporate-tpa`}
          />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setEditDialog(null)}
              style={{ backgroundColor: "#ccc" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default CorporateTpaList;
